import {
  createSlice,
  PayloadAction
} from "@reduxjs/toolkit";
import { TransformExecuteResponse } from "../../types/clientTypes";
import {
  TestPlan,
  TestPlanScenario
} from "../../types/testPlanTypes";
import { Scenario } from "../../types/scenarioTypes";
import {
  TestCaseTemplate,
  TransformTestCase
} from "../../types/testCaseTemplateTypes";
import {
  getTemplateAndTestCaseByTestCaseId,
  getTransformTestCase
} from "../../utils/testCaseUtils";
import { SelectedVariable } from "../../types/variableType";

const initialState: {
  selectedTestPlan: TestPlan,
  selectedScenarios: Array<TestPlanScenario>,
  editTestCaseId?: number,
  testPlanExecuteResponses: Array<TransformExecuteResponse>
} = {
  selectedTestPlan: {
    id: 0,
    name: "",
    assignedScenarios: [],
    description: "",
    maximumExecutionTimeout: 0
  },
  selectedScenarios: [],
  testPlanExecuteResponses: []
}

const testPlanSlice = createSlice({
  name: "testPlanSlice",
  initialState,
  reducers: {
    setTestPlan: (state, action: PayloadAction<{ testPlan: TestPlan, scenarios: Array<Scenario>, templates: Array<TestCaseTemplate>, selectedVariables: Array<SelectedVariable> }>) => {
      const testPlan = action.payload.testPlan
      const scenarios = action.payload.scenarios;
      const testCaseTemplates = action.payload.templates;
      state.selectedTestPlan = testPlan;

      const defaultTestCase: TransformTestCase = {
        name: "",
        testCaseTemplateId: 0,
        requestParameterValues: [],
        responseParameterValues: []
      }
      const defaultScenario: TestPlanScenario = {
        name: "",
        assignedRequests: [],
        maximumExecutionTimeout: 0,
        queueNumber: -1,
        internalId: "",
        testCases: []
      }
      state.selectedScenarios = testPlan.assignedScenarios.map(assignedScenario => {
        const findScenario = scenarios?.find(item => item.id === assignedScenario.scenarioId);
        if (!!findScenario) {
          const testCases: Array<TransformTestCase> = findScenario.assignedRequests.map(request => {
            const testCaseAndTemplate = getTemplateAndTestCaseByTestCaseId(testCaseTemplates || [], request.requestId);
            if (!!testCaseAndTemplate) {
              let testCase = getTransformTestCase(action.payload.selectedVariables, testCaseAndTemplate.template, testCaseAndTemplate.testCase);
              testCase = { ...testCase };
              testCase.internalId = `${assignedScenario.id}-${request.id}`;
              return testCase;
            } else {
              return defaultTestCase;
            }
          });
          return {
            ...findScenario,
            queueNumber: assignedScenario.queueNumber,
            internalId: assignedScenario.id?.toString() || "",
            testCases: testCases
          };
        } else {
          return defaultScenario;
        }
      });
    },
    setEditTestCaseId: (state, action: PayloadAction<number | undefined>) => {
      state.editTestCaseId = action.payload;
    },
    updateTestPlanSelectedScenarios: (state, action: PayloadAction<Array<TestPlanScenario>>) => {
      state.selectedScenarios = action.payload;
    },
    deleteTestPlan(state) {
      state.selectedTestPlan = {
        id: 0,
        name: "",
        assignedScenarios: [],
        description: "",
        maximumExecutionTimeout: 0
      };
      state.testPlanExecuteResponses = [];
    },
    deleteTestPlanExecuteResponse(state) {
      state.testPlanExecuteResponses = [];
    },
    deleteTestPlanExecuteResponseByIds(state, action: PayloadAction<Array<string | undefined>>) {
      const internalIds = action.payload;
      state.testPlanExecuteResponses = state.testPlanExecuteResponses.map(item => {
        if (internalIds.includes(item.internalId)) {
          return {
            statusCode: 0,
            responseBody: "",
            time: 0,
            internalId: item.internalId
          }
        } else {
          return item;
        }
      });
    },
    setTestPlanExecuteResponse: (state, action: PayloadAction<Array<TransformExecuteResponse>>) => {
      state.testPlanExecuteResponses = action.payload
    },
    updateTestPlanExecuteResponse: (state, action: PayloadAction<TransformExecuteResponse>) => {
      const executeResponse = action.payload;
      let newExecuteResponse = state.testPlanExecuteResponses.find(item => item.internalId === executeResponse.internalId);
      if (newExecuteResponse) {
        newExecuteResponse.responseBody = executeResponse.responseBody;
        newExecuteResponse.isLoad = executeResponse.isLoad || false;
        newExecuteResponse.time = executeResponse.time;
        newExecuteResponse.statusCode = executeResponse.statusCode;
        newExecuteResponse.isError = executeResponse.isError;
        newExecuteResponse.isLoadTestrail = executeResponse.isLoadTestrail;
        newExecuteResponse.testrailResult = executeResponse.testrailResult;
        newExecuteResponse.result = executeResponse.result;
        newExecuteResponse.validationErrorMessage = executeResponse.validationErrorMessage;
      } else {
        state.testPlanExecuteResponses.push(executeResponse);
      }
    },
  }
});

export const {
  setTestPlan,
  setEditTestCaseId,
  updateTestPlanSelectedScenarios,
  deleteTestPlan,
  setTestPlanExecuteResponse,
  deleteTestPlanExecuteResponse,
  deleteTestPlanExecuteResponseByIds,
  updateTestPlanExecuteResponse
} = testPlanSlice.actions;

export default testPlanSlice.reducer;